import * as React from 'react';
import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import { ProfileGalleryWidget } from './ProfileGalleryWidget';
import { useParams } from 'react-router-dom';
import { TabWidget } from '../../../../widgets/controls/TabWidget';
import { useSystem } from '../../../../hooks/useSystem';
import { ProfileModel } from '../../../../core/models/ProfileModel';
import { ProfileHeaderPage } from './ProfileHeaderWidget';
import { ProfilePacksWidget } from './ProfilePacksWidget';
import { AnimatePageWidget } from '../../../../widgets/layout/AnimatePageWidget';
import { useResponsive } from '../../../../hooks/useResponsive';
import { LoadingWidget } from '../../../../widgets/content/LoadingWidget';
import { ProfileGroupsWidget } from './ProfileGroupsWidget';
import { AboutSectionWidget } from '../../../../widgets/pages/AboutSectionWidget';
import { DesktopSectionWidget } from '../../../../widgets/pages/DesktopSectionWidget';

export enum ProfileTabs {
  ABOUT = 'about',
  GALLERY = 'gallery',
  PACKS = 'packs',
  GROUPS = 'groups'
}
export const ProfilePage: React.FC = observer(() => {
  const system = useSystem();
  let params = useParams<'id'>();
  const [user, setUser] = useState<ProfileModel | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await system.waitForUser();
      if (cancelled) {
        return;
      }
      const resolved = system.user_store.getModel(params.id);
      await resolved.refresh();
      if (!cancelled) {
        setUser(resolved);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params.id, system]);

  let { desktop } = useResponsive();

  return (
    <AnimatePageWidget slide={true}>
      <LoadingWidget loading={!user?.data}>
        {() =>
          user ? desktop ? <DesktopProfileWidget profile={user} /> : <MobileProfileWidget profile={user} /> : null
        }
      </LoadingWidget>
    </AnimatePageWidget>
  );
});

export interface MobileProfileWidgetProps {
  profile: ProfileModel;
}

export const MobileProfileWidget: React.FC<MobileProfileWidgetProps> = (props) => {
  const { profile } = props;
  const [tab, setTab] = useState<ProfileTabs>(ProfileTabs.ABOUT);
  return (
    <>
      <ProfileHeaderPage user={profile} />
      <S.Section>
        <TabWidget
          tabs={Object.values(ProfileTabs)}
          selected={tab}
          onChange={(selected) => {
            setTab(selected as ProfileTabs);
          }}
        />
        {tab === ProfileTabs.ABOUT ? <AboutSectionWidget model={profile} /> : null}
        {tab === ProfileTabs.GALLERY ? <ProfileGalleryWidget profile={profile} /> : null}
        {tab === ProfileTabs.PACKS ? <ProfilePacksWidget profile={profile} /> : null}
        {tab === ProfileTabs.GROUPS ? <ProfileGroupsWidget profile={profile} /> : null}
      </S.Section>
    </>
  );
};

export interface DesktopProfileWidgetProps {
  profile: ProfileModel;
}

export const DesktopProfileWidget: React.FC<DesktopProfileWidgetProps> = (props) => {
  const { profile } = props;
  return (
    <S.Desktop>
      <S.DesktopLeft>
        <ProfileHeaderPage user={profile} />
        <S.Section>
          <AboutSectionWidget model={profile} />
        </S.Section>
      </S.DesktopLeft>
      <S.DesktopRight>
        <DesktopSectionWidget label="Gallery">
          <ProfileGalleryWidget profile={profile} />
        </DesktopSectionWidget>
        <DesktopSectionWidget label="Packs">
          <ProfilePacksWidget profile={profile} />
        </DesktopSectionWidget>
        <DesktopSectionWidget label="Groups">
          <ProfileGroupsWidget profile={profile} />
        </DesktopSectionWidget>
      </S.DesktopRight>
    </S.Desktop>
  );
};

namespace S {
  export const Container = styled.div``;

  export const Desktop = styled.div`
    display: flex;
    height: 100%;
  `;

  export const DesktopLeft = styled.div`
    width: 400px;
    overflow: auto;
    border-right: solid 1px ${(p) => p.theme.layout.border};
  `;

  export const DesktopRight = styled.div`
    flex-grow: 1;
    padding: 10px;
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    overflow: auto;
  `;
}

namespace S {
  const PADDING = 20;

  export const Section = styled.div`
    padding: ${PADDING}px;
    display: flex;
    flex-direction: column;
    row-gap: 10px;
  `;
}
